import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function ContactPage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 100);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message') {
      if (value.length > 500) return;
      setCharCount(value.length);
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const body = new URLSearchParams();
      body.append('firstName', formData.firstName);
      body.append('lastName', formData.lastName);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('message', formData.message);

      const res = await fetch('https://readdy.ai/api/form/d6g8ka8r225iqdhs0m2g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      });

      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ firstName: '', lastName: '', email: '', phone: '', message: '' });
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
          scrolled ? 'bg-black border-b border-[#C9A961]' : 'bg-black/95 backdrop-blur-md'
        }`}
        style={{ height: '80px' }}
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-[60px] lg:px-[120px] h-full flex items-center justify-between">
          <div className="flex items-center">
            <Link to="/">
              <img
                src="https://static.readdy.ai/image/b8716fa8a6d7792031f552f74d12f7bc/83934bc45ac34654febf39565a6f5f5c.png"
                alt="Elite Property Operations"
                className="h-16 md:h-20 lg:h-24 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-12">
            <Link to="/#services" className="text-[13px] uppercase tracking-[1.2px] text-[#6B6B6B] hover:text-[#C9A961] transition-colors duration-300 relative group whitespace-nowrap">
              Services
              <span className="absolute bottom-[-4px] left-0 w-0 h-[2px] bg-[#C9A961] group-hover:w-full transition-all duration-300"></span>
            </Link>
            <Link to="/#programs" className="text-[13px] uppercase tracking-[1.2px] text-[#6B6B6B] hover:text-[#C9A961] transition-colors duration-300 relative group whitespace-nowrap">
              Contracting
              <span className="absolute bottom-[-4px] left-0 w-0 h-[2px] bg-[#C9A961] group-hover:w-full transition-all duration-300"></span>
            </Link>
            <Link to="/#why-us" className="text-[13px] uppercase tracking-[1.2px] text-[#6B6B6B] hover:text-[#C9A961] transition-colors duration-300 relative group whitespace-nowrap">
              Why Us
              <span className="absolute bottom-[-4px] left-0 w-0 h-[2px] bg-[#C9A961] group-hover:w-full transition-all duration-300"></span>
            </Link>
            <Link to="/#regions" className="text-[13px] uppercase tracking-[1.2px] text-[#6B6B6B] hover:text-[#C9A961] transition-colors duration-300 relative group whitespace-nowrap">
              Regions
              <span className="absolute bottom-[-4px] left-0 w-0 h-[2px] bg-[#C9A961] group-hover:w-full transition-all duration-300"></span>
            </Link>
            <Link to="/contact" className="text-[13px] uppercase tracking-[1.2px] text-[#C9A961] transition-colors duration-300 relative group whitespace-nowrap">
              Contact
              <span className="absolute bottom-[-4px] left-0 w-full h-[2px] bg-[#C9A961]"></span>
            </Link>
          </div>
          <Link
            to="/contact"
            className="hidden lg:flex h-[44px] px-8 border border-[#C9A961] bg-transparent text-[#F8F8F8] text-[14px] font-medium items-center justify-center rounded-sm hover:bg-[#C9A961] hover:text-black transition-all duration-400 whitespace-nowrap cursor-pointer"
          >
            Schedule Assessment
          </Link>

          {/* Mobile Hamburger */}
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center text-[#F8F8F8] cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-[28px]`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden bg-black border-t border-[#2C2C2C] px-6 py-6 flex flex-col gap-5">
            <Link to="/#services" onClick={() => setMenuOpen(false)} className="text-[13px] uppercase tracking-[1.2px] text-[#6B6B6B] hover:text-[#C9A961] transition-colors">Services</Link>
            <Link to="/#programs" onClick={() => setMenuOpen(false)} className="text-[13px] uppercase tracking-[1.2px] text-[#6B6B6B] hover:text-[#C9A961] transition-colors">Contracting</Link>
            <Link to="/#why-us" onClick={() => setMenuOpen(false)} className="text-[13px] uppercase tracking-[1.2px] text-[#6B6B6B] hover:text-[#C9A961] transition-colors">Why Us</Link>
            <Link to="/#regions" onClick={() => setMenuOpen(false)} className="text-[13px] uppercase tracking-[1.2px] text-[#6B6B6B] hover:text-[#C9A961] transition-colors">Regions</Link>
            <Link to="/contact" onClick={() => setMenuOpen(false)} className="text-[13px] uppercase tracking-[1.2px] text-[#C9A961] transition-colors">Contact</Link>
            <Link to="/contact" onClick={() => setMenuOpen(false)} className="h-[44px] px-6 border border-[#C9A961] bg-transparent text-[#F8F8F8] text-[13px] font-medium flex items-center justify-center rounded-sm hover:bg-[#C9A961] hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer w-fit">
              Schedule Assessment
            </Link>
          </div>
        )}
      </nav>

      {/* Page Header */}
      <section className="relative bg-[#0A0A0A] pt-[140px] md:pt-[180px] pb-[70px] md:pb-[100px] overflow-hidden" style={{ marginTop: '80px' }}>
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/d55e59338dfafc12a637a87dbbd1d154.jpg"
            alt="Elite Property Operations Contact"
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/60 via-[#0A0A0A]/70 to-[#0A0A0A]"></div>
        </div>
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-[60px] lg:px-[120px] text-center">
          <p className="text-[11px] uppercase tracking-[2px] text-[#C9A961] mb-5 font-medium">GET IN TOUCH</p>
          <h1 className="text-[32px] md:text-[48px] lg:text-[64px] leading-[1.1] text-[#F8F8F8] font-light mb-6">
            Contact Elite Property Operations
          </h1>
          <p className="text-[15px] md:text-[18px] text-[#B8B8B8] font-light max-w-[620px] mx-auto leading-[1.7]">
            Connect with our operations team to schedule an assessment, request a proposal, or discuss your property needs.
          </p>
          <div className="w-16 h-[2px] bg-[#C9A961] mx-auto mt-10"></div>
        </div>
      </section>

      {/* Primary Action Panels */}
      <section className="bg-[#F8F8F8] py-[60px] md:py-[80px]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-[60px] lg:px-[120px]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {/* Panel 1 */}
            <div className="bg-white border border-[#E0E0E0] rounded-sm p-8 md:p-12 flex flex-col gap-6 hover:border-[#C9A961] hover:shadow-[0_8px_40px_rgba(201,169,97,0.12)] transition-all duration-400 group">
              <div className="w-12 h-12 flex items-center justify-center">
                <i className="ri-home-4-line text-[36px] md:text-[40px] text-[#C9A961]"></i>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[2px] text-[#C9A961] mb-3">RESIDENTIAL & ESTATE</p>
                <h2 className="text-[22px] md:text-[28px] text-black font-light leading-[1.2] mb-4">
                  Schedule Property Assessment
                </h2>
                <p className="text-[14px] md:text-[15px] text-[#6B6B6B] leading-[1.7]">
                  Request a structured on-site evaluation of your residential or estate property. Our operations team will assess your needs and deliver a comprehensive service proposal.
                </p>
              </div>
              <a
                href="#inquiry-form"
                className="mt-auto h-[48px] px-6 md:px-8 bg-black text-[#F8F8F8] text-[12px] md:text-[13px] uppercase tracking-[1px] font-medium flex items-center justify-center rounded-sm hover:bg-[#C9A961] hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer w-fit gap-2"
              >
                Schedule Assessment
                <i className="ri-arrow-right-line text-[15px]"></i>
              </a>
            </div>

            {/* Panel 2 */}
            <div className="bg-[#0A0A0A] border border-[#2C2C2C] rounded-sm p-8 md:p-12 flex flex-col gap-6 hover:border-[#C9A961] hover:shadow-[0_8px_40px_rgba(201,169,97,0.12)] transition-all duration-400 group">
              <div className="w-12 h-12 flex items-center justify-center">
                <i className="ri-building-2-line text-[36px] md:text-[40px] text-[#C9A961]"></i>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[2px] text-[#C9A961] mb-3">COMMERCIAL & INSTITUTIONAL</p>
                <h2 className="text-[22px] md:text-[28px] text-[#F8F8F8] font-light leading-[1.2] mb-4">
                  Request Commercial Proposal
                </h2>
                <p className="text-[14px] md:text-[15px] text-[#B8B8B8] leading-[1.7]">
                  Submit a formal request for commercial, HOA, or institutional service proposals. We provide structured contracts tailored to multi-property portfolios and municipal facilities.
                </p>
              </div>
              <a
                href="#inquiry-form"
                className="mt-auto h-[48px] px-6 md:px-8 border border-[#C9A961] bg-transparent text-[#C9A961] text-[12px] md:text-[13px] uppercase tracking-[1px] font-medium flex items-center justify-center rounded-sm hover:bg-[#C9A961] hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer w-fit gap-2"
              >
                Request Proposal
                <i className="ri-arrow-right-line text-[15px]"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Form + Contact Info */}
      <section id="inquiry-form" className="bg-white py-[80px] md:py-[120px]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-[60px] lg:px-[120px]">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-[6%] items-start">

            {/* Form */}
            <div className="w-full lg:w-[58%]">
              <p className="text-[11px] uppercase tracking-[2px] text-[#C9A961] mb-4">GENERAL INQUIRY</p>
              <h2 className="text-[28px] md:text-[40px] text-black font-light leading-[1.2] mb-2">Submit Your Inquiry</h2>
              <div className="w-12 h-[2px] bg-[#C9A961] mb-10"></div>

              {submitStatus === 'success' ? (
                <div className="bg-[#F8F8F8] border border-[#C9A961] rounded-sm p-8 md:p-12 text-center">
                  <div className="w-14 h-14 flex items-center justify-center mx-auto mb-6">
                    <i className="ri-checkbox-circle-line text-[52px] text-[#C9A961]"></i>
                  </div>
                  <h3 className="text-[22px] md:text-[24px] text-black font-light mb-4">Inquiry Received</h3>
                  <p className="text-[15px] md:text-[16px] text-[#2C2C2C] leading-[1.7] max-w-[480px] mx-auto">
                    Thank you for contacting Elite Property Operations™. A member of our operations team will review your request and respond promptly.
                  </p>
                  <button
                    onClick={() => setSubmitStatus('idle')}
                    className="mt-8 h-[44px] px-8 border border-[#C9A961] bg-transparent text-black text-[13px] uppercase tracking-[1px] font-medium rounded-sm hover:bg-[#C9A961] transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Submit Another Inquiry
                  </button>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-2">
                      <label className="text-[12px] uppercase tracking-[1px] text-[#6B6B6B]">First Name <span className="text-[#C9A961]">*</span></label>
                      <input
                        type="text"
                        name="firstName"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="John"
                        className="h-[50px] px-4 border border-[#E0E0E0] rounded-sm text-[14px] text-black bg-[#FAFAFA] focus:outline-none focus:border-[#C9A961] transition-colors duration-200 placeholder:text-[#ABABAB]"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[12px] uppercase tracking-[1px] text-[#6B6B6B]">Last Name <span className="text-[#C9A961]">*</span></label>
                      <input
                        type="text"
                        name="lastName"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Smith"
                        className="h-[50px] px-4 border border-[#E0E0E0] rounded-sm text-[14px] text-black bg-[#FAFAFA] focus:outline-none focus:border-[#C9A961] transition-colors duration-200 placeholder:text-[#ABABAB]"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-[12px] uppercase tracking-[1px] text-[#6B6B6B]">Email Address <span className="text-[#C9A961]">*</span></label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john.smith@example.com"
                      className="h-[50px] px-4 border border-[#E0E0E0] rounded-sm text-[14px] text-black bg-[#FAFAFA] focus:outline-none focus:border-[#C9A961] transition-colors duration-200 placeholder:text-[#ABABAB]"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-[12px] uppercase tracking-[1px] text-[#6B6B6B]">Phone Number <span className="text-[#C9A961]">*</span></label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(310) 000-0000"
                      className="h-[50px] px-4 border border-[#E0E0E0] rounded-sm text-[14px] text-black bg-[#FAFAFA] focus:outline-none focus:border-[#C9A961] transition-colors duration-200 placeholder:text-[#ABABAB]"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-[12px] uppercase tracking-[1px] text-[#6B6B6B]">
                      What Would You Like Elite Property Operations to Assist With?
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                      placeholder="Please describe the services you require, property details, project scope, or any specific concerns."
                      className="px-4 py-3 border border-[#E0E0E0] rounded-sm text-[14px] text-black bg-[#FAFAFA] focus:outline-none focus:border-[#C9A961] transition-colors duration-200 placeholder:text-[#ABABAB] resize-none leading-[1.6]"
                    />
                    <p className={`text-[12px] text-right ${charCount >= 500 ? 'text-red-500' : 'text-[#ABABAB]'}`}>
                      {charCount}/500
                    </p>
                  </div>

                  {submitStatus === 'error' && (
                    <p className="text-[13px] text-red-500">
                      There was an issue submitting your inquiry. Please try again or contact us directly.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting || charCount > 500}
                    className="h-[52px] px-8 md:px-10 bg-[#C9A961] text-black text-[13px] uppercase tracking-[1.2px] font-semibold rounded-sm hover:bg-[#b8943f] transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-3"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-[16px]"></i>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Inquiry
                        <i className="ri-arrow-right-line text-[16px]"></i>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="w-full lg:w-[36%] lg:sticky lg:top-[100px]">
              <p className="text-[11px] uppercase tracking-[2px] text-[#C9A961] mb-4">DIRECT CONTACT</p>
              <h2 className="text-[24px] md:text-[32px] text-black font-light leading-[1.2] mb-2">Direct Contact Information</h2>
              <div className="w-12 h-[2px] bg-[#C9A961] mb-10"></div>

              <div className="space-y-8">
                <div className="flex items-start gap-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#0A0A0A] rounded-sm flex-shrink-0">
                    <i className="ri-mail-line text-[20px] text-[#C9A961]"></i>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-[1px] text-[#6B6B6B] mb-1">Email</p>
                    <a
                      href="mailto:info@docgod.com"
                      className="text-[14px] md:text-[15px] text-black hover:text-[#C9A961] transition-colors duration-200 cursor-pointer break-all"
                    >
                      info@docgod.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#0A0A0A] rounded-sm flex-shrink-0">
                    <i className="ri-phone-line text-[20px] text-[#C9A961]"></i>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-[1px] text-[#6B6B6B] mb-1">Phone</p>
                    <a
                      href="tel:+13107212271"
                      className="text-[14px] md:text-[15px] text-black hover:text-[#C9A961] transition-colors duration-200 cursor-pointer"
                    >
                      (310) 721-2271
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#0A0A0A] rounded-sm flex-shrink-0">
                    <i className="ri-time-line text-[20px] text-[#C9A961]"></i>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-[1px] text-[#6B6B6B] mb-1">Response Time</p>
                    <p className="text-[14px] md:text-[15px] text-black leading-[1.6]">
                      Our operations team responds within 24 business hours.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#0A0A0A] rounded-sm flex-shrink-0">
                    <i className="ri-map-pin-line text-[20px] text-[#C9A961]"></i>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-[1px] text-[#6B6B6B] mb-1">Service Area</p>
                    <p className="text-[14px] md:text-[15px] text-black leading-[1.6]">
                      West Los Angeles, Beverly Hills, Brentwood, Santa Monica, Marina del Rey &amp; Greater Los Angeles
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 md:mt-12 bg-[#0A0A0A] rounded-sm p-6 md:p-8 border-l-2 border-[#C9A961]">
                <p className="text-[13px] text-[#B8B8B8] leading-[1.7] italic">
                  "Elite Property Operations™ delivers disciplined, systemized maintenance and asset preservation&nbsp; operating with precision and oversight to ensure long-term performance."
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-[#C9A961] py-14 md:py-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-[60px] lg:px-[120px]">
          <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-10 md:mb-12">
            <div className="w-full md:w-[40%]">
              <p className="text-[18px] md:text-[20px] text-[#F8F8F8] font-light mb-4">Elite Property Operations</p>
              <p className="text-[13px] md:text-[14px] text-[#B8B8B8] italic">Structured Precision. Elevated Standards.</p>
            </div>
            <div className="w-full md:w-[30%]">
              <div className="space-y-4">
                <a href="tel:+13107212271" className="block text-[14px] md:text-[15px] text-[#F8F8F8] hover:text-[#C9A961] hover:underline transition-colors cursor-pointer">
                  (310) 721-2271
                </a>
                <a href="mailto:info@docgod.com" className="block text-[14px] md:text-[15px] text-[#F8F8F8] hover:text-[#C9A961] hover:underline transition-colors cursor-pointer">
                  info@docgod.com
                </a>
              </div>
            </div>
            <div className="w-full md:w-[30%] md:flex md:flex-col md:items-end gap-4">
              <Link
                to="/contact"
                className="flex items-center justify-center cursor-pointer"
              >
                <img
                  src="https://static.readdy.ai/image/b8716fa8a6d7792031f552f74d12f7bc/83934bc45ac34654febf39565a6f5f5c.png"
                  alt="Elite Property Operations"
                  className="h-32 w-auto"
                />
              </Link>
            </div>
          </div>
          <div className="text-center pt-10 md:pt-12 border-t border-[#2C2C2C]">
            <p className="text-[11px] md:text-[12px] text-[#6B6B6B]">
              © 2026 Elite Property Operations™. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
